import * as THREE from "three";
import { Line2, LineGeometry, LineMaterial } from "three-fatline";
import Experience from "../Experience";
import { activeLineW, defaultLineW, lineDark, lineLight } from "../Utils/styles";

type Vector3Like = { x: number; y: number; z: number };
type LineEndpoint = THREE.Object3D | Vector3Like | null | undefined;
type LinePositions = { sourceCenter: THREE.Vector3; targetCenter: THREE.Vector3 };

export default class LineElement {
  experience!: Experience;
  scene!: THREE.Scene;
  source: LineEndpoint;
  sizes: { width: number; height: number } = { width: 0, height: 0 };
  target: LineEndpoint;
  color: THREE.ColorRepresentation = lineDark;
  opacity = 0.5;
  isBetweenInteracted = false;
  positions: LinePositions | null = null;
  direction: THREE.Vector3 = new THREE.Vector3();
  distance = 0;
  geometry!: LineGeometry;
  material!: LineMaterial;
  line!: Line2;

  constructor(source: LineEndpoint, target: LineEndpoint, color: THREE.ColorRepresentation = lineDark, isBetweenInteracted = false) {
    this.experience = new Experience();
    this.scene = this.experience.scene;
    this.source = source;
    this.sizes = this.experience.sizes;
    this.target = target;
    this.color = color;
    this.opacity = 0.5;
    this.isBetweenInteracted = isBetweenInteracted;
    this.create();
  }

  create() {
    this.positions = this.getPositions();
    this.direction = this.getDirection();
    this.distance = this.getDistance();
    this.geometry = this.setLineGeometry();
    this.material = this.setLineMaterial();
    this.line = this.setLine();
    this.setLineData();
    this.setDirOnTarget();
    return this.line;
  }

  getPositions() {
    let sourceCenter: THREE.Vector3;
    let targetCenter: THREE.Vector3;

    if (this.source instanceof THREE.Object3D) {
      sourceCenter = new THREE.Vector3();
      this.source.getWorldPosition(sourceCenter);
    } else if (
      typeof this.source === "object" &&
      this.source !== null &&
      typeof this.source.x === "number" &&
      typeof this.source.y === "number" &&
      typeof this.source.z === "number"
    ) {
      sourceCenter = new THREE.Vector3(this.source.x, this.source.y, this.source.z);
    } else {
      throw new Error("Invalid source type");
    }
    if (this.target instanceof THREE.Object3D) {
      targetCenter = new THREE.Vector3();
      this.target.getWorldPosition(targetCenter);
    } else if (
      typeof this.target === "object" &&
      this.target !== null &&
      typeof this.target.x === "number" &&
      typeof this.target.y === "number" &&
      typeof this.target.z === "number"
    ) {
      targetCenter = new THREE.Vector3(this.target.x, this.target.y, this.target.z);
    } else {
      throw new Error("Invalid target type");
    }

    if (
      !isNaN(sourceCenter.x) &&
      !isNaN(sourceCenter.y) &&
      !isNaN(sourceCenter.z) &&
      !isNaN(targetCenter.x) &&
      !isNaN(targetCenter.y) &&
      !isNaN(targetCenter.z)
    ) {
      return { sourceCenter, targetCenter };
    } else {
      console.error("NaN detected in position values", { sourceCenter, targetCenter });
      return null;
    }
  }

  setLineGeometry() {
    if (!this.positions) throw new Error("Line positions are not initialized");
    const line2Geometry = new LineGeometry();
    line2Geometry.setPositions([
      this.positions.sourceCenter.x,
      this.positions.sourceCenter.y,
      this.positions.sourceCenter.z,
      this.positions.targetCenter.x,
      this.positions.targetCenter.y,
      this.positions.targetCenter.z,
    ]);
    return line2Geometry;
  }

  setLineMaterial() {
    let lineMaterial: LineMaterial;
    const lineColor = new THREE.Color(this.color).getHex();

    if (this.isBetweenInteracted) {
      lineMaterial = new LineMaterial({
        color: lineColor,
        linewidth: Number(activeLineW),
        opacity: 1,
        transparent: true,
        resolution: new THREE.Vector2(this.sizes.width, this.sizes.height),
      });
    } else {
      if (document.body.classList.contains("day") || document.body.classList.contains("dawn")) {
        this.color = lineLight;
      } else if (document.body.classList.contains("night") || document.body.classList.contains("dusk")) {
        this.color = lineDark;
      }

      lineMaterial = new LineMaterial({
        color: lineColor,
        linewidth: Number(defaultLineW),
        opacity: 0.5,
        transparent: true,
        resolution: new THREE.Vector2(this.sizes.width, this.sizes.height),
      });
    }
    return lineMaterial;
  }

  setLine() {
    const line = new Line2(this.geometry, this.material);
    line.computeLineDistances();
    line.name = this.isBetweenInteracted ? "PathLine" : "CrossroadLine";
    line.frustumCulled = true;
    this.scene.add(line);
    return line;
  }

  setLineData() {
    this.line.userData.isBetweenInteracted = this.isBetweenInteracted;
    this.line.userData.positions = this.positions;
  }

  getDirection() {
    if (!this.positions) return new THREE.Vector3(0, 0, 0);
    const direction = new THREE.Vector3()
      .copy(this.positions.targetCenter)
      .sub(this.positions.sourceCenter)
      .normalize();
    return direction;
  }

  getDistance() {
    if (!this.positions) return 0;
    return this.positions.sourceCenter.distanceTo(this.positions.targetCenter);
  }

  getExtendedTarget() {
    if (!this.positions || !this.direction) return new THREE.Vector3();
    const extendFactor = 0.1;
    const extendedTarget = new THREE.Vector3()
      .copy(this.positions.sourceCenter)
      .add(this.direction.clone().multiplyScalar(extendFactor));
    return extendedTarget;
  }

  setDirOnTarget() {
    if (this.target instanceof THREE.Object3D) {
      this.target.userData.direction = this.direction;
    }
  }
}
